import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Search,
  Play,
  Clock,
  Trash2,
  Filter,
  PlayCircle,
  Youtube,
  ExternalLink,
  GraduationCap,
  Library
} from 'lucide-react';
import TutorialsTab from '../components/TutorialsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['EHR/Practice Management', 'Branded App', 'Tutorials', 'CCM', 'APCM', 'RPM', 'Annual Wellness', 'Product Training', 'Compliance'];

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const Videos = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    duration: '',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos`, getAuthHeaders());
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVideo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/videos`, {
        ...newVideo,
        duration: newVideo.duration ? parseInt(newVideo.duration) : null
      }, getAuthHeaders());
      toast.success('Video added');
      setShowCreateModal(false);
      setNewVideo({ title: '', description: '', video_url: '', thumbnail_url: '', duration: '', category: '', tags: [] });
      fetchVideos();
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/videos/${id}`, getAuthHeaders());
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newVideo.tags.includes(tagInput.trim())) {
      setNewVideo({ ...newVideo, tags: [...newVideo.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewVideo({ ...newVideo, tags: newVideo.tags.filter(t => t !== tag) });
  };

  const filteredVideos = videos.filter(v => {
    const matchesSearch = !searchQuery || 
      v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || v.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="videos-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Video Library</h1>
          <p className="text-slate-500 mt-1">Training videos, product demos, and tutorials</p>
        </div>
        {isAdmin && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="add-video-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Video
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Video</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateVideo} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                    placeholder="Introduction to EHR"
                    required
                    data-testid="video-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                    placeholder="Brief description of this video"
                    data-testid="video-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Video URL (YouTube embed) *</Label>
                  <Input
                    value={newVideo.video_url}
                    onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/embed/..."
                    required
                    data-testid="video-url"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Thumbnail URL</Label>
                  <Input
                    value={newVideo.thumbnail_url}
                    onChange={(e) => setNewVideo({ ...newVideo, thumbnail_url: e.target.value })}
                    placeholder="https://example.com/thumbnail.jpg"
                    data-testid="video-thumbnail"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (seconds)</Label>
                    <Input
                      type="number"
                      value={newVideo.duration}
                      onChange={(e) => setNewVideo({ ...newVideo, duration: e.target.value })}
                      placeholder="300"
                      data-testid="video-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select 
                      value={newVideo.category} 
                      onValueChange={(v) => setNewVideo({ ...newVideo, category: v })}
                    >
                      <SelectTrigger data-testid="video-category">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newVideo.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                        {tag} x
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="submit-video-btn">
                    Add Video
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* YouTube Channel Banner */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-red-600 to-red-500 text-white overflow-hidden" data-testid="youtube-banner">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Youtube className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium">Visit Enable Healthcare's YouTube Channel for more videos</p>
              <p className="text-white/80 text-sm">Subscribe for the latest product updates, tutorials, and training content</p>
            </div>
          </div>
          <Button 
            asChild
            className="bg-white text-red-600 hover:bg-white/90 rounded-full shadow-lg whitespace-nowrap flex-shrink-0"
            data-testid="youtube-channel-btn"
          >
            <a href="https://www.youtube.com/@enablehealthcare1/videos" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Channel
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* Tabs: Library / Tutorials */}
      <Tabs defaultValue="library" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-full">
          <TabsTrigger value="library" className="rounded-full data-[state=active]:bg-white gap-2" data-testid="tab-library">
            <Library className="w-4 h-4" />
            Library
          </TabsTrigger>
          <TabsTrigger value="tutorials" className="rounded-full data-[state=active]:bg-white gap-2" data-testid="tab-tutorials">
            <GraduationCap className="w-4 h-4" />
            Tutorials
          </TabsTrigger>
        </TabsList>

        {/* Library Tab */}
        <TabsContent value="library">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full"
                data-testid="search-videos"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48 rounded-full" data-testid="video-category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Video Player Modal */}
          <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
            <DialogContent className="max-w-4xl p-0 overflow-hidden">
              {selectedVideo && (
                <>
                  <div className="aspect-video">
                    <iframe
                      src={selectedVideo.video_url}
                      title={selectedVideo.title}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-6">
                    <h2 className="font-heading text-xl font-semibold">{selectedVideo.title}</h2>
                    <p className="text-slate-500 mt-2">{selectedVideo.description}</p>
                    <div className="flex items-center gap-3 mt-4">
                      <Badge variant="outline">{selectedVideo.category}</Badge>
                      {selectedVideo.duration && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          {formatDuration(selectedVideo.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Videos Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-slate-500">Loading videos...</div>
            </div>
          ) : filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map(video => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card 
                    className="border-0 shadow-apple hover:shadow-lg transition-shadow cursor-pointer overflow-hidden group"
                    onClick={() => setSelectedVideo(video)}
                    data-testid={`video-${video.id}`}
                  >
                    <div className="relative aspect-video bg-slate-100">
                      {video.thumbnail_url ? (
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1B365D] to-[#0071E3]">
                          <PlayCircle className="w-16 h-16 text-white/80" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      {video.duration && (
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-white text-xs">
                          {formatDuration(video.duration)}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-slate-900 line-clamp-1">{video.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                        {video.description || 'No description'}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <Badge variant="secondary">{video.category}</Badge>
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-600 h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVideo(video.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-apple">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <PlayCircle className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">No videos found</h3>
                <p className="text-slate-500">
                  {searchQuery || categoryFilter !== 'all' 
                    ? 'Try adjusting your search or filter'
                    : 'Training videos will appear here once added.'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tutorials Tab */}
        <TabsContent value="tutorials">
          <TutorialsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Videos;
